/** @file SlotDropZone: drop or pick a PNG for a slot; a size mismatch asks Pad / Scale / Cancel first. */
import type { ChangeEvent, DragEvent, ReactElement, ReactNode, Ref } from 'react';
import { useState } from 'react';
import type { FitMode } from './artTypes';
import { fitChoice } from './fitChoice';
import { readPngSize } from './readPngSize';

/** The slot key and size, what to do with a chosen PNG, where to report a bad file, the picker input's ref. */
export type SlotDropZoneProps = {
  slotKey: string; slotSize: [number, number];
  onFill: (png: Uint8Array, fit?: FitMode) => void; onError: (message: string) => void;
  inputRef?: Ref<HTMLInputElement>; children: ReactNode;
};

/** A dropped or picked file waiting for the user's fit choice. */
type Pending = { png: Uint8Array; size: [number, number] };

const BUTTON = { padding: '2px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #555', background: '#2f333b', color: '#eee', cursor: 'pointer' };

/** The prompt shown over the card when the image size differs from the slot's. */
function FitPrompt({ pending, slotSize, choose }: { pending: Pending; slotSize: [number, number]; choose: (fit: FitMode | null) => void }): ReactElement {
  return (
    <div role="dialog" aria-label="Image size differs" style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center', background: 'rgba(16,18,22,0.92)', borderRadius: 6, fontSize: 11, textAlign: 'center', padding: 4 }}>
      <span>{pending.size[0]}x{pending.size[1]} image, {slotSize[0]}x{slotSize[1]} slot</span>
      <div style={{ display: 'flex', gap: 4 }}>
        <button type="button" style={BUTTON} onClick={(e) => { e.stopPropagation(); choose('pad'); }}>Pad</button>
        <button type="button" style={BUTTON} onClick={(e) => { e.stopPropagation(); choose('scale'); }}>Scale</button>
        <button type="button" style={BUTTON} onClick={(e) => { e.stopPropagation(); choose(null); }}>Cancel</button>
      </div>
    </div>
  );
}

/** Reads the file's PNG size: the slot's size fills at once, any other size waits for Pad / Scale / Cancel. */
export function SlotDropZone({ slotKey, slotSize, onFill, onError, inputRef, children }: SlotDropZoneProps): ReactElement {
  const [pending, setPending] = useState<Pending | null>(null);
  const [over, setOver] = useState(false);
  const take = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    const png = new Uint8Array(await file.arrayBuffer());
    let size: [number, number];
    try {
      size = readPngSize(png);
    } catch (e) {
      onError(`${file.name} is not a PNG: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    if (fitChoice(size, slotSize) === 'exact') onFill(png);
    else setPending({ png, size });
  };
  const drop = (e: DragEvent): void => {
    setOver(false);
    const files = [...e.dataTransfer.files];
    const [only] = files;
    // One PNG is this slot's; several files or a zip bubble up to the screen's bulk upload by file name.
    if (files.length !== 1 || only === undefined || /\.zip$/i.test(only.name)) return;
    e.preventDefault();
    e.stopPropagation();
    void take(only);
  };
  const picked = (e: ChangeEvent<HTMLInputElement>): void => {
    void take(e.target.files?.[0]);
    e.target.value = '';
  };
  const choose = (fit: FitMode | null): void => {
    if (pending !== null && fit !== null) onFill(pending.png, fit);
    setPending(null);
  };
  return (
    <div
      data-dropzone={slotKey}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => { setOver(false); }}
      onDrop={drop}
      style={{ position: 'relative', outline: over ? '2px dashed #5b8def' : 'none', borderRadius: 6 }}
    >
      {children}
      {pending === null ? null : <FitPrompt pending={pending} slotSize={slotSize} choose={choose} />}
      <input ref={inputRef} type="file" accept="image/png" aria-label={`Upload PNG for ${slotKey}`} hidden onChange={picked} />
    </div>
  );
}
